import {
    Avatar,
    Box,
    Breadcrumbs,
    Button,
    Container,
    Divider,
    Grid,
    IconButton,
    Link,
    Modal,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import React, { useState } from "react";
import * as Yup from "yup";

import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useNavigate, useParams } from "react-router-dom";
import queryGraphql from "../../../helpers/ApiInterface";
import { useAuth } from "../../../helpers/AuthProvider";
import { Close } from "@mui/icons-material";
import { Formik, ErrorMessage, Form } from "formik";

import CompulsoryFieldsLabel from "../../../helpers/CompulsoryFields";
import MiniBot from "../../MiniBot";
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../../MainDrawer";

// --- Interfaces ---
interface SolutionOwnerInfo {
    name: string;
}

interface DataplexLakeNestedDetails { // Changed interface name
    // User-submitted fields
    projectId: string;
    location: string;
    description: string;
    // No resourceSpec or discoverySpec for a lake itself
}

interface DataLakeDetailsData { // Changed interface name
    id: string;
    name: string;
    solutionCategoryType: string;
    status: string;
    cloudProvider: 'gcp' | 'aws' | 'azure' | string;
    details: {
        dataplexLake: DataplexLakeNestedDetails; // Changed field name
    };
    solutionOwner: SolutionOwnerInfo;
    reasonForRejection?: string;
}

// --- Validation Schemas ---
// Approval no longer requires input, so no schema needed for approval.

const rejectValidationSchema = Yup.object().shape({
    reasonForRejection: Yup.string().required("Reason for rejection is required").min(5, "Reason must be at least 5 characters long"),
});

// --- Helper Component ---
const DetailItem: React.FC<{ label: string; value?: string | number | boolean | null }> = ({ label, value }) => {
    const displayValue = value !== null && value !== undefined && value !== '' ? value : '-';
    return (
        <Box mb={2}>
            <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{label}</Typography>
            <Typography fontFamily="Gilroy-Semibold" fontSize="14px" sx={{ wordBreak: 'break-all' }}>{typeof displayValue === 'boolean' ? (displayValue ? 'Enabled' : 'Disabled') : displayValue}</Typography>
        </Box>
    );
};

// --- Main Content ---
const DataLakesApproveFormPageContent: React.FC = () => { // Changed component name
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { user } = useAuth();
    const [dataLake, setDataLake] = useState<DataLakeDetailsData | null>(null); // Changed state variable name
    const [solutionEndpoint, setSolutionEndpoint] = useState<string | null>(null);
    const [openRejectModal, setOpenRejectModal] = useState(false);
    const [openApproveModal, setOpenApproveModal] = useState(false);
    const [dynamicLabels, setDynamicLabels] = useState({
        projectId: 'Project ID',
    });
    const fallback = '-';

    React.useEffect(() => {
        const fetchData = async () => {
            if (!id) return;
            try {
                const data = await queryGraphql(
                    `
                    query GetDataplexLake($getDataplexLakeId: String!) { # Changed query name
                      getDataplexLake(id: $getDataplexLakeId) { # Changed field name
                        id, name, solutionCategoryType, status, reasonForRejection, cloudProvider
                        details { 
                          dataplexLake { # Changed from dataSources
                            projectId, location, description
                          } 
                        }
                        solutionOwner { id, name }
                      }
                    }
                    `, { getDataplexLakeId: id } // Changed variable name
                );
                if (data.getDataplexLake) { // Changed field name
                    setDataLake(data.getDataplexLake); // Changed state variable name
                } else {
                    console.log("Data Lake details not found."); // Changed text
                    setDataLake(null);
                }
            } catch (error) {
                console.log("Error fetching Data Lake details:", error); // Changed text
            }
        };
        fetchData();
    }, [id]);

    React.useEffect(() => {
        if (dataLake?.cloudProvider) { // Changed state variable name
            switch (dataLake.cloudProvider) { // Changed state variable name
                case 'aws':
                    setDynamicLabels({ projectId: 'Account ID' });
                    break;
                case 'azure':
                    setDynamicLabels({ projectId: 'Subscription ID' });
                    break;
                case 'gcp':
                default:
                    setDynamicLabels({ projectId: 'Project ID' });
                    break;
            }
        }
    }, [dataLake]); // Changed dependency


    React.useEffect(() => {
        const fetchEndpointUrl = async () => {
            if (!dataLake?.id) return; // Changed state variable name
            try {
                const data = await queryGraphql(
                    `query ResourceStatus($resourceStatusId: String!) {
                      resourceStatus(id: $resourceStatusId) { id, solutionEndpointUrl }
                    }`, { resourceStatusId: dataLake.id } // Changed state variable name
                );
                if (data.resourceStatus?.solutionEndpointUrl) {
                    setSolutionEndpoint(data.resourceStatus.solutionEndpointUrl);
                }
            } catch (error) {
                console.log("Error fetching solution endpoint URL:", error);
            }
        };
        fetchEndpointUrl();
    }, [dataLake]); // Changed dependency

    const statusDisplay = dataLake?.status ? dataLake.status : "Status Unknown"; // Changed state variable name
    const statusBgColor = dataLake?.status === "active" || dataLake?.status === "approved" ? "#12B76A10" : dataLake?.status === "rejected" ? "#F0443810" : "#FDB02210"; // Changed state variable name
    const statusIcon = dataLake?.status === "active" || dataLake?.status === "approved" ? "/assets/tickcircle.svg" : dataLake?.status === "rejected" ? "/assets/reject.svg" : "/assets/clock.svg"; // Changed state variable name

    return (
        <Container disableGutters maxWidth={false} sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
            <MiniBot />
            <Box sx={{ p: 2, bgcolor: "#FFFFFF" }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
                    <Link underline="hover" color="inherit" href="/datasolutions/requests/data-lakes" onClick={(e) => { e.preventDefault(); navigate("/datasolutions/requests/data-lakes") }}> {/* Changed navigation path */}
                        Data Lake Requests {/* Changed text */}
                    </Link>
                    <Typography color="text.primary">Data Lake Details</Typography> {/* Changed text */}
                </Breadcrumbs>
                <Box display="flex" alignItems="center" mt={1}>
                    <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/datasolutions/requests/data-lakes")} > {/* Changed navigation path */}
                        <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                    <Typography fontFamily="Gilroy-Bold" fontSize="18px">Data Lake Details</Typography> {/* Changed text */}
                </Box>
            </Box>
            <Grid container justifyContent="center">
                <Grid item xs={11} md={11} lg={11}>
                    {dataLake && ( // Changed state variable name
                        <Formik
                            initialValues={{}} // No initial values needed
                            onSubmit={async () => {
                                // No payload details needed for approval anymore
                                try {
                                    const approvalData = await queryGraphql(
                                        `mutation ApproveSolutionDataplexLake($input: ApproveSolutionInput!) { approveSolutionDataplexLake(input: $input) { id name status } }`, // Changed mutation name
                                        { input: { id: dataLake.id } } // Changed state variable name
                                    );
                                    if (approvalData.approveSolutionDataplexLake?.id) { // Changed field name
                                        setOpenApproveModal(false);
                                        navigate("/datasolutions/requests/data-lakes"); // Changed navigation path
                                    } else {
                                        console.error("Approval failed", approvalData);
                                    }
                                } catch (error) {
                                    console.error("Error during approval:", error);
                                }
                            }}
                        >
                            {({ }) => (
                                <Form id="approve-dataLake-main-form"> {/* Changed form id */}
                                    <Box my={2}>
                                        <Box bgcolor={statusBgColor} borderRadius="12px 12px 0px 0px" p={2} boxShadow={1}>
                                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap">
                                                <Box sx={{ mb: { xs: 1, sm: 0 } }}>
                                                    <Typography variant="h5" fontFamily="Gilroy-Bold" gutterBottom>{dataLake.name || fallback}</Typography> {/* Changed state variable name */}
                                                    <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{`Resource Owner: ${dataLake.solutionOwner?.name || fallback}`}</Typography> {/* Changed state variable name */}
                                                </Box>
                                                <Box textAlign="right">
                                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: 'flex-end' }}>
                                                        <img src={statusIcon} alt="Status Icon" style={{ width: "20px", marginRight: "8px" }} />
                                                        <Typography fontSize="16px" fontFamily="Gilroy-SemiBold" sx={{ textTransform: 'capitalize' }}>{statusDisplay}</Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                        
                                        <Box bgcolor="#fff" p={3} borderRadius="0px 0px 12px 12px" boxShadow={1}>
                                            <Grid container spacing={2.5}>
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label={dynamicLabels.projectId} value={dataLake.details.dataplexLake.projectId} /></Grid> {/* Changed state variable name */}
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Location" value={(dataLake.details.dataplexLake.location)} /></Grid> {/* Changed state variable name */}
                                                <Grid item xs={12} sm={6} md={3}><DetailItem label="Cloud Provider" value={dataLake.cloudProvider?.toUpperCase()} /></Grid> {/* Changed state variable name */}
                                                {solutionEndpoint && (<Grid item xs={12} sm={6} md={3}><Typography variant="caption" display="block" sx={{ fontFamily: 'Gilroy-Regular', fontSize: '12px', color: '#666666' }}>Solution Endpoint</Typography><Tooltip title={<Link href={solutionEndpoint} target="_blank" rel="noopener noreferrer" sx={{ color: '#ffffff' }}>{solutionEndpoint}</Link>}><Avatar src="/assets/info.svg" alt="info" sx={{ height: 16, width: 16, cursor: 'pointer' }} /></Tooltip></Grid>)}
                                                
                                                <Grid item xs={12}><Divider/></Grid>
                                                
                                                <Grid item xs={12}><DetailItem label="Description" value={dataLake.details.dataplexLake.description} /></Grid> {/* Changed state variable name */}
                                            </Grid>
                                        </Box>

                                        {dataLake.status === 'rejected' && ( // Changed state variable name
                                             <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow={1}>
                                                <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Rejection Details</Typography>
                                                <Divider sx={{ marginY: 2 }} />
                                                <DetailItem label="Reason for Rejection" value={dataLake.reasonForRejection} /> {/* Changed state variable name */}
                                             </Box>
                                        )}

                                    </Box>
                                    
                                    {dataLake?.status === "pending" && user.roles.some((role: string) => ["infrastructure_admin"].includes(role)) && ( // Changed state variable name
                                        <Box display="flex" justifyContent="flex-end" gap={2} mt={3} mb={3}>
                                            <Button sx={{ color: "black", borderColor: "black", fontWeight: "bold", textTransform: "none", px: 4, fontFamily: "Gilroy-Bold" }} variant="outlined" size="large" onClick={() => setOpenRejectModal(true)}>Reject Request</Button>
                                            <Button sx={{ backgroundColor: "black", color: "white", fontWeight: "bold", "&:hover": { backgroundColor: "rgba(0,0,0,0.85)"}, textTransform: "none", px: 4, fontFamily: "Gilroy-Bold", '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' } }} variant="contained" size="large" onClick={() => setOpenApproveModal(true)}>Approve Request</Button>
                                        </Box>
                                    )}
                                </Form>
                            )}
                        </Formik>
                    )}
                </Grid>
            </Grid>

            {/* Simple Confirmation Modal for Approval */}
            <Modal open={openApproveModal} onClose={() => setOpenApproveModal(false)}>
                <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: { xs: '90%', md: 500 }, bgcolor: "white", boxShadow: 24, borderRadius: "16px" }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
                        <Typography fontFamily="Gilroy-SemiBold" fontSize="20px">Approve Data Lake Request</Typography> {/* Changed text */}
                        <IconButton aria-label="close" onClick={() => setOpenApproveModal(false)}><Close fontSize="small" /></IconButton>
                    </Box>
                    <Divider />
                    <Typography p={3}>Are you sure you want to approve this Data Lake request?</Typography> {/* Changed text */}
                    <Divider />
                    <Box sx={{ p: 2, display: 'flex', justifyContent: "flex-end", gap: 1 }}>
                        <Button variant="outlined" onClick={() => setOpenApproveModal(false)} sx={{ color: 'black', borderColor: 'black', textTransform: 'none' }}>Cancel</Button>
                        <Button type="submit" form="approve-dataLake-main-form" sx={{ backgroundColor: "black", color: "white", textTransform: "none", "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" } }} variant="contained">Approve</Button>
                    </Box>
                </Box>
            </Modal>

            {/* Rejection Modal with its own Formik instance */}
            <Modal open={openRejectModal} onClose={() => setOpenRejectModal(false)}>
                <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: { xs: '90%', md: 700 }, bgcolor: "white", boxShadow: 24, borderRadius: "16px", display: 'flex', flexDirection: 'column' }}>
                    <Formik
                        initialValues={{ reasonForRejection: "" }}
                        validationSchema={rejectValidationSchema}
                        onSubmit={async ({ reasonForRejection }) => {
                            if (!id) return;
                            try {
                                const rejectionData = await queryGraphql(
                                    `mutation RejectSolutionDataplexLake($input: RejectSolutionInput!) { rejectSolutionDataplexLake(input: $input) { id name } }`, // Changed mutation name
                                    { input: { id: id, reasonForRejection } }
                                );
                                if (rejectionData.rejectSolutionDataplexLake?.id) { // Changed field name
                                    setOpenRejectModal(false);
                                    navigate("/datasolutions/requests/data-lakes"); // Changed navigation path
                                }
                            } catch (error) { console.error("Error during rejection:", error); }
                        }}
                    >
                        {({ handleChange, handleBlur, values, errors, touched, isValid, dirty }) => (
                            <Form id="reject-dataLake-form" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}> {/* Changed form id */}
                                <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
                                    <Typography fontFamily="Gilroy-SemiBold" fontSize="20px">Reject Data Lake Request</Typography> {/* Changed text */}
                                    <IconButton aria-label="close" onClick={() => setOpenRejectModal(false)}><Close fontSize="small" /></IconButton>
                                </Box>
                                <Divider />
                                <Box sx={{ p: 3, flexGrow: 1 }}>
                                    <CompulsoryFieldsLabel fieldName="Comments" />
                                    <TextField variant="filled" fullWidth multiline rows={4} name="reasonForRejection" value={values.reasonForRejection} onChange={handleChange} onBlur={handleBlur} placeholder="Enter comments for rejection..." hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} error={touched.reasonForRejection && Boolean(errors.reasonForRejection)} />
                                    <Box sx={{ color: "red", mt: 1 }}><ErrorMessage name="reasonForRejection" /></Box>
                                </Box>
                                <Divider />
                                <Box sx={{ p: 2, display: 'flex', justifyContent: "flex-end", gap: 1 }}>
                                    <Button variant="outlined" onClick={() => setOpenRejectModal(false)} sx={{ color: 'black', borderColor: 'black', textTransform: 'none' }}>Cancel</Button>
                                    <Button type="submit" form="reject-dataLake-form" disabled={!isValid || !dirty} sx={{ backgroundColor: "black", color: "white", textTransform: "none", "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" }, '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' } }} variant="contained">Reject</Button>
                                </Box>
                            </Form>
                        )}
                    </Formik>
                </Box>
            </Modal>
        </Container>
    );
};

const DataLakesApproveFormPage: React.FC = () => { // Changed component name
    const theme = useTheme();
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(() => typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('isFaradayDrawerOpen') || 'true') : true);
    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
        if (typeof window !== 'undefined') localStorage.setItem('isFaradayDrawerOpen', JSON.stringify(isOpenFromDrawer));
    };
    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer index={"requests-data-lakes"} onStateChange={handleDrawerStateChange} /> {/* Changed index */}
            <Box component="main" sx={{ flexGrow: 1, bgcolor: '#fafafa', p: 0, transition: theme.transitions.create('margin', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen, }), marginLeft: { xs: 0, sm: `${currentMarginLeft}px` } }}>
                <DataLakesApproveFormPageContent /> {/* Changed component */}
            </Box>
        </Box>
    );
};

export default DataLakesApproveFormPage;